import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useCMS } from '../context/CMSContext';
import { getDisplayCities, getSelectableCities, isUuid } from '../constants/cities';
import { apiUrl } from '../lib/apiBase';
import { isInCooldown, setCooldown, markSessionSuccess, hasSessionSuccess } from '../lib/leadCooldown';
import {
  markLeadCaptured,
  isLeadCapturedForPhone,
  getCapturedLeadId,
  clearLeadCaptured,
} from '../lib/otpLeadSession';

const { FiUser, FiPhone, FiMapPin, FiChevronDown, FiCheckCircle } = FiIcons;

const DEFAULT_CITIES = [
  "Delhi", "Noida", "Gurgaon", "Ghaziabad", "Faridabad", "Vadodara", "Ahmedabad"
];

const DEFAULT_CITY_ENTRIES = DEFAULT_CITIES.map((name) => ({ id: null, name }));

const getUrlSlug = () => {
  try {
    if (typeof window === 'undefined') return '';
    const pathname = window.location.pathname;
    if (pathname.includes('/lead/')) {
      const match = pathname.match(/\/lead\/(.*)/);
      if (match && match[1]) {
        return match[1].replace(/^\/+|\/+$/g, '').substring(0, 100);
      }
    }
    let slug = pathname.replace(/^\/+|\/+$/g, '');
    slug = slug.replace(/[^a-zA-Z0-9\-_]/g, '');
    return slug.substring(0, 100) || '';
  } catch {
    return '';
  }
};

const buildLeadPayload = (formData, pageTemplate) => {
  const searchParams = new URLSearchParams(window.location.search);
  const urlParams = {};
  searchParams.forEach((value, key) => {
    urlParams[key] = value;
  });

  return {
    name: formData.name,
    phone: formData.phone,
    city: formData.cityName || formData.city || '',
    slug: getUrlSlug(),
    referralUrl: window.location.href,
    pageTemplate: pageTemplate || undefined,
    urlParams,
    utmSource: searchParams.get('utm_source'),
    utmMedium: searchParams.get('utm_medium'),
    utmTerm: searchParams.get('utm_term'),
    utmCampaign: searchParams.get('utm_campaign'),
    utmAdset: searchParams.get('utm_adset'),
    utmContent: searchParams.get('utm_content'),
    utmId: searchParams.get('utm_id'),
    utmKeyword: searchParams.get('Keyword'),
    utmKeywordId: searchParams.get('Keyword_Id'),
    gclid: searchParams.get('gclid') || searchParams.get('gcl_Id'),
    fbclid: searchParams.get('fbclid'),
    adId: searchParams.get('ad_id'),
    adgroupId: searchParams.get('adgroup_id'),
    adsetId: searchParams.get('adset_id'),
    campaignId: searchParams.get('campaign_id') || searchParams.get('gad_campaignid'),
    locationId: searchParams.get('Location'),
    placement: searchParams.get('placement'),
  };
};

const OtpInput = ({ value, onChange, disabled }) => {
  const inputsRef = useRef([]);
  const digits = value.padEnd(6, ' ').split('').slice(0, 6);

  const focusInput = (index) => {
    inputsRef.current[index]?.focus();
  };

  const handleChange = (index, char) => {
    if (!/^\d?$/.test(char)) return;
    const arr = value.split('');
    arr[index] = char;
    const next = arr.join('').slice(0, 6);
    onChange(next);
    if (char && index < 5) focusInput(index + 1);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index]?.trim() && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted) onChange(pasted);
  };

  return (
    <div className="flex justify-center gap-2" onPaste={handlePaste}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={digit.trim()}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          className="w-10 h-12 text-center text-lg font-semibold border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-100 disabled:text-gray-400"
        />
      ))}
    </div>
  );
};

const ActionErrorBanner = ({ message, hint, waitSeconds = 0, onRetry, retryLabel = 'Try again' }) => (
  <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1.5">
    <p className="text-xs text-red-700 text-center leading-relaxed">{message}</p>
    {hint && (
      <p className="text-xs text-red-600/90 text-center leading-relaxed">{hint}</p>
    )}
    {waitSeconds > 0 && (
      <p className="text-xs text-red-600 text-center font-semibold">
        {onRetry ? `Try again in ${waitSeconds}s` : `You can tap Verify again in ${waitSeconds}s`}
      </p>
    )}
    {!hint && waitSeconds <= 0 && onRetry ? (
      <button
        type="button"
        onClick={onRetry}
        className="w-full text-sm font-semibold text-[#143a69] py-2 rounded-lg border border-[#143a69]/20 bg-white hover:bg-gray-50 transition-colors"
      >
        {retryLabel}
      </button>
    ) : null}
  </div>
);

const FormPanel = ({
  hero,
  cities,
  otpConfig,
  formData,
  isBookingTemplate,
  onInputChange,
  formStep,
  isFormValid,
  isSubmitting,
  otpValue,
  otpError,
  otpErrorHint,
  otpRetriesLeft,
  resendTimer,
  leadFormError,
  actionRetryTimer,
  onSubmit,
  onOtpChange,
  onOtpVerify,
  onOtpResend,
  isMobile,
  selectedItemLabel,
  phoneError,
}) => {
  const submitLabel = isBookingTemplate ? 'BOOK NOW' : 'SUBMIT';
  const isLocked = formStep === 'success' || isSubmitting;

  if (isSubmitting && formStep === 'lead') {
    return (
      <div className={`${isMobile ? 'px-5 pb-5' : 'px-6 pb-6'}`}>
        <div className="flex flex-col items-center justify-center text-center py-10 px-4 min-h-[280px]">
          <div className="w-14 h-14 rounded-full border-4 border-[#143a69]/15 border-t-[#143a69] animate-spin mb-4" />
          <h3 className="text-lg font-bold text-[#143a69] leading-tight">
            {isBookingTemplate ? 'Redirecting you to book your test ...' : 'Sending OTP to your phone...'}
          </h3>
          <p className="text-xs text-gray-500 mt-2">This will only take a moment.</p>
        </div>
      </div>
    );
  }

  if (formStep === 'success') {
    return (
      <div className={`${isMobile ? 'px-5 pb-5' : 'px-6 pb-6'}`}>
        <div className="flex flex-col items-center justify-center text-center py-10 px-4 min-h-[280px]">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-[#143a69] leading-tight">
            Request received! Our agents will contact you soon.
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isMobile ? 'px-5 pb-5 space-y-3' : 'px-6 pb-6 space-y-4'} relative`}>
      <div className="bg-white border border-green-200 rounded-xl p-3 flex items-center gap-3 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
        <div className="w-10 h-10 rounded-full bg-orange-100 shrink-0 flex items-center justify-center overflow-hidden">
          <span className="text-xl">🥗</span>
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold text-slate-800">{hero.offerTitle}</p>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-gray-500">{hero.offerSubtitle}</span>
            <div className="text-right">
              <span className="text-green-600 font-bold text-sm">Free</span>
              <span className="text-gray-400 line-through text-[10px] ml-1">{hero.offerPriceOriginal}</span>
            </div>
          </div>
        </div>
      </div>

      {formStep === 'otp' ? (
        <div className="space-y-4 py-2">
          <div className="text-center">
            <h3 className="text-base font-bold text-slate-800">Verify your phone number</h3>
            <p className="text-xs text-gray-500 mt-1">
              Enter the 6-digit OTP sent to +91 {formData.phone}
            </p>
          </div>

          <OtpInput value={otpValue} onChange={onOtpChange} disabled={isSubmitting || otpRetriesLeft === 0} />

          {otpError && (
            <ActionErrorBanner
              message={otpError}
              hint={otpErrorHint}
              waitSeconds={actionRetryTimer}
            />
          )}

          {otpRetriesLeft !== null && otpRetriesLeft <= 1 && otpRetriesLeft > 0 && (
            <p className="text-xs text-amber-600 text-center">Last attempt remaining</p>
          )}

          <button
            type="button"
            onClick={onOtpVerify}
            disabled={otpValue.length !== 6 || isSubmitting || otpRetriesLeft === 0}
            className={`w-full font-bold py-3 rounded-full uppercase tracking-wider transition-colors shadow-sm text-base ${otpValue.length === 6 && !isSubmitting && otpRetriesLeft !== 0
                ? 'bg-[#143a69] hover:bg-[#0f2d52] text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {isSubmitting ? 'Verifying...' : 'Verify'}
          </button>

          <button
            type="button"
            onClick={onOtpResend}
            disabled={resendTimer > 0 || isSubmitting}
            className="w-full text-sm font-medium text-[#143a69] hover:text-[#0f2d52] disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
          </button>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={onSubmit}>
          {selectedItemLabel && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
              <SafeIcon icon={FiCheckCircle} className="text-blue-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-blue-600 font-semibold uppercase tracking-wide leading-none mb-0.5">
                  Selected Package
                </p>
                <p className="text-xs font-bold text-slate-800 truncate">{selectedItemLabel}</p>
              </div>
            </div>
          )}
          {leadFormError && (
            <ActionErrorBanner
              message={leadFormError.message}
              hint={isBookingTemplate ? 'Check your details and tap Book Now below.' : 'Check your details and tap Submit below.'}
              waitSeconds={actionRetryTimer}
            />
          )}

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SafeIcon icon={FiUser} className="text-gray-400" />
            </div>
            <input
              type="text"
              name="name"
              id={isMobile ? 'hero-name-input-mobile' : 'hero-name-input-desktop'}
              placeholder="Enter Name"
              className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-full text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder-gray-400 bg-white"
              value={formData.name}
              onChange={onInputChange}
              disabled={isLocked}
            />
          </div>

          <div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-gray-400 font-medium text-sm">+91</span>
              </div>
              <input
                type="tel"
                name="phone"
                placeholder="Enter Phone Number"
                className={`w-full pl-12 pr-4 py-3 border rounded-full text-sm focus:ring-1 outline-none transition-all placeholder-gray-400 bg-white ${phoneError
                    ? 'border-red-400 focus:ring-red-400 focus:border-red-400'
                    : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                  }`}
                value={formData.phone}
                onChange={onInputChange}
                maxLength={10}
                inputMode="numeric"
                disabled={isLocked}
              />
            </div>
            {phoneError && (
              <p className="text-red-500 text-xs mt-1 pl-4">{phoneError}</p>
            )}
          </div>

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SafeIcon icon={FiMapPin} className="text-gray-400" />
            </div>
            <select
              name="city"
              className="w-full pl-11 pr-10 py-3 border border-gray-200 rounded-full text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white appearance-none cursor-pointer"
              value={formData.cityId || formData.cityName || ''}
              onChange={onInputChange}
              style={{ color: (formData.cityId || formData.cityName) ? '#1f2937' : '#9ca3af' }}
              disabled={isLocked}
            >
              <option value="" disabled>Select City</option>
              {cities.map((city) => (
                <option
                  key={city.id ?? city.name}
                  value={city.id ?? city.name}
                  style={{ color: '#1f2937' }}
                >
                  {city.name}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
              <SafeIcon icon={FiChevronDown} className="text-gray-400" />
            </div>
          </div>

          <button
            type="submit"
            disabled={!isFormValid || isLocked}
            className={`w-full font-bold py-3 rounded-full uppercase tracking-wider transition-colors shadow-sm text-base mt-4 ${isFormValid && !isLocked
                ? 'bg-[#143a69] hover:bg-[#0f2d52] text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
          >
            {isSubmitting ? 'Please wait...' : submitLabel}
          </button>
          {isBookingTemplate && !isLocked && (formData.cityName || formData.cityId) && !isUuid(formData.cityId) && (
            <p className="text-xs text-red-600 text-center pt-1">
              This page's city isn't fully set up for booking yet — ask your CMS admin to
              re-select it from the IAM city list in Form Settings.
            </p>
          )}
          <p className="text-[9px] text-gray-400 text-center pt-1">
            *Prices are subject to change as per city
          </p>
        </form>
      )}
    </div>
  );
};

const HeroSection = ({ selectedPackage }) => {
  const { data, activeTemplate } = useCMS();
  const { hero, formData: formSettings = {} } = data;

  const otpEnabled = activeTemplate === 'otp' || Boolean(formSettings.otpEnabled);
  const otpConfig = {
    expiryMinutes: formSettings.otpExpiryMinutes ?? 5,
    maxRetries: formSettings.otpMaxRetries ?? 3,
    resendCooldownSeconds: formSettings.otpResendCooldownSeconds ?? 30,
    maxResends: formSettings.otpMaxResends ?? 3,
  };

  const isBookingTemplate = activeTemplate === 'booking';

  const configuredCities = isBookingTemplate
    ? getSelectableCities(formSettings.cities)
    : getDisplayCities(formSettings.cities);
  const cities = configuredCities.length > 0 ? configuredCities : DEFAULT_CITY_ENTRIES;

  const [formData, setFormData] = useState({ name: '', phone: '', cityId: '', cityName: '' });

  const [formStep, setFormStep] = useState(() =>
    (hasSessionSuccess() || isInCooldown(undefined, activeTemplate)) ? 'success' : 'lead'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isSmallDevice, setIsSmallDevice] = useState(false);
  const [brokenImages, setBrokenImages] = useState({});

  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpErrorHint, setOtpErrorHint] = useState('');
  const [leadFormError, setLeadFormError] = useState(null);
  const [otpRetriesLeft, setOtpRetriesLeft] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [actionRetryTimer, setActionRetryTimer] = useState(0);
  const [phoneError, setPhoneError] = useState('');

  const submitInFlightRef = useRef(false);

  const isFormValid = formData.name.trim() !== '' &&
    formData.phone.length === 10 &&
    /^\d+$/.test(formData.phone) &&
    (isBookingTemplate
      ? isUuid(formData.cityId)
      : (formData.cityName || '').trim() !== '');

  useEffect(() => {
    const syncCooldownState = () => {
      if (hasSessionSuccess() || isInCooldown(undefined, activeTemplate)) setFormStep('success');
    };
    syncCooldownState();
    window.addEventListener('curelo-lead-submitted', syncCooldownState);
    return () => window.removeEventListener('curelo-lead-submitted', syncCooldownState);
  }, []);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  useEffect(() => {
    if (actionRetryTimer <= 0) return;
    const timer = setTimeout(() => setActionRetryTimer((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [actionRetryTimer]);

  const applyRetryCooldown = (waitSeconds) => {
    if (waitSeconds && waitSeconds > 0) {
      setActionRetryTimer(waitSeconds);
    }
  };

  const OTP_VERIFY_HINT = 'Enter the correct 6-digit OTP and tap Verify.';
  const OTP_RESEND_HINT = 'Tap Resend OTP below to receive a new code.';

  const clearOtpErrors = () => {
    setOtpError('');
    setOtpErrorHint('');
    setActionRetryTimer(0);
  };

  const handleOtpChange = (value) => {
    setOtpValue(value);
    if (otpError) clearOtpErrors();
  };

  const setOtpSendError = (message, waitSeconds) => {
    setOtpError(message || 'OTP could not be sent. Please try again.');
    setOtpErrorHint(OTP_RESEND_HINT);
    if (waitSeconds) applyRetryCooldown(waitSeconds);
  };

  const setOtpVerifyError = (message, waitSeconds) => {
    const msg = message || 'OTP verification failed.';
    setOtpError(msg);
    const lower = msg.toLowerCase();
    if (lower.includes('resend otp') || lower.includes('expired')) {
      setOtpErrorHint(OTP_RESEND_HINT);
    } else if (lower.includes('submit the form')) {
      setOtpErrorHint('Refresh the page and submit the form again to request a new OTP.');
    } else {
      setOtpErrorHint(OTP_VERIFY_HINT);
    }
    if (waitSeconds) applyRetryCooldown(waitSeconds);
  };

  useEffect(() => {
    const checkScreenSize = () => setIsSmallDevice(window.innerWidth < 400);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleImageError = (key) => {
    setBrokenImages((prev) => ({ ...prev, [key]: true }));
  };

  const getImageSrc = (imgSrc, key) => {
    if (brokenImages[key] || !imgSrc) {
      return 'https://phlebo-india.s3.ap-south-1.amazonaws.com/curelo-health/hero.png';
    }
    return imgSrc;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      // Strip non-digits and cap at 10
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      if (digitsOnly !== formData.phone) {
        clearLeadCaptured(formData.phone);
        setLeadFormError(null);
        setActionRetryTimer(0);
        if (formStep === 'success' && !isInCooldown(digitsOnly, activeTemplate)) {
          setFormStep('lead');
        }
      }
      // Inline validation feedback
      if (digitsOnly.length > 0 && digitsOnly.length < 10) {
        setPhoneError('Please enter a valid 10-digit number.');
      } else {
        setPhoneError('');
      }
      setFormData((prev) => ({ ...prev, phone: digitsOnly }));
      return;
    }
    if (name === 'city') {
      const selected = cities.find(
        (city) => String(city.id ?? city.name) === String(value)
      );
      setFormData((prev) => ({
        ...prev,
        cityId: selected?.id != null ? String(selected.id) : (selected?.name || value),
        cityName: selected?.name || value,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const sendOtp = useCallback(async (resend = false) => {
    const response = await fetch(apiUrl('/api/otp/send'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone: formData.phone,
        resend,
        expiryMinutes: otpConfig.expiryMinutes,
        resendCooldownSeconds: otpConfig.resendCooldownSeconds,
        maxResends: otpConfig.maxResends,
      }),
    });
    const result = await response.json();
    if (!response.ok) {
      if (result.waitSeconds) {
        setResendTimer(result.waitSeconds);
        applyRetryCooldown(result.waitSeconds);
      }
      const err = new Error(result.message || 'OTP could not be sent. Please try again.');
      err.waitSeconds = result.waitSeconds;
      throw err;
    }
    setResendTimer(otpConfig.resendCooldownSeconds);
    return result;
  }, [formData.phone, otpConfig]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitInFlightRef.current || isSubmitting) return;

    if (formStep === 'success') {
      return;
    }

    if (!formData.name.trim()) { alert('Please enter your name'); return; }
    if (!formData.phone || !/^\d{10}$/.test(formData.phone)) { alert('Please enter a valid 10-digit phone number'); return; }

    if (isInCooldown(formData.phone, activeTemplate)) {
      setFormStep('success');
      return;
    }

    if (!formData.cityName && !formData.cityId) { alert('Please select your city'); return; }
    if (isBookingTemplate && !isUuid(formData.cityId)) {
      alert('Please select a city with a valid ID. Ask your admin to re-select cities from the IAM list in CMS.');
      return;
    }
    // A specific "Book Now" click (MostBookedPackages → onPackageSelect) wins
    // over the page's single default booking_item_id — that default only
    // applies when the patient used the hero form directly without picking a
    // package card. See MostBookedPackages.jsx's onClick for how
    // selectedPackage gets set.
    const selectedCatalogId = selectedPackage?.catalogId || formSettings.booking_item_id;

    if (isBookingTemplate && !selectedCatalogId) {
      alert('Booking product is not configured for this page. Please contact support.');
      return;
    }

    if (isBookingTemplate) {
      const catalogId = Number(selectedCatalogId);
      if (!Number.isInteger(catalogId)) {
        alert('Booking product is not configured correctly. Please contact support.');
        return;
      }

      if (submitInFlightRef.current || isSubmitting) return;
      submitInFlightRef.current = true;
      setIsSubmitting(true);
      try {
        // referralUrl/urlParams carry UTM/gclid attribution through to the
        // booking-session gateway call cms-backend makes alongside createCart
        // — same extraction buildLeadPayload already does for the non-booking
        // /api/lead path, reused here rather than duplicated.
        const attribution = buildLeadPayload(formData, activeTemplate);
        const cartBody = {
          phone: formData.phone,
          name: formData.name,
          cityId: String(formData.cityId).trim(),
          // cityName (not cityId) is what should reach LeadSquared as
          // mx_Patient_City — cityId is a booking-api catalog UUID, not
          // something meaningful to show in the CRM.
          cityName: formData.cityName || undefined,
          source: 'CMS',
          itemType: selectedPackage?.itemType || formSettings.booking_item_type || 'test',
          catalogId,
          referralUrl: attribution.referralUrl,
          urlParams: attribution.urlParams,
        };
        if (formSettings.promoCode?.trim()) {
          cartBody.promoCode = formSettings.promoCode.trim();
        }

        const cartResponse = await fetch(apiUrl('/api/cart/redirect'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cartBody),
        });

        const cartResult = await cartResponse.json();

        if (cartResponse.ok && cartResult.redirect_url) {
          // Only start the cooldown once we know the booking actually kicked
          // off — setting it earlier would block a legitimate retry after a
          // failed cart creation with no way to reach the booking portal.
          // (No-ops for the booking template anyway — see leadCooldown.js.)
          setCooldown(formData.phone, activeTemplate);
          window.location.href = cartResult.redirect_url;
          return;
        }

        console.error('Cart API failed:', cartResult);
        alert(cartResult.error || 'Could not start your booking right now. Please try again in a moment.');
      } catch (error) {
        console.error('Booking submit failed:', error);
        alert('Could not start your booking right now. Please check your connection and try again.');
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
      return;
    }

    const payload = buildLeadPayload(formData, activeTemplate);

    if (!otpEnabled) {
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;
      setIsSubmitting(true);

      setCooldown(formData.phone, activeTemplate);
      markSessionSuccess();
      setFormStep('success');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);

      try {
        await fetch(apiUrl('/api/lead'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        });
      } catch (error) {
        console.error('Background lead submission failed:', error);
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setLeadFormError(null);
    setOtpError('');
    setOtpErrorHint('');
    setActionRetryTimer(0);

    const skipLeadCapture = isLeadCapturedForPhone(formData.phone);

    try {
      if (!skipLeadCapture) {
        const leadResponse = await fetch(apiUrl('/api/lead'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, mxOtpVerified: 'No', skipLeadCapture: false }),
        });
        const leadResult = await leadResponse.json();
        if (!leadResponse.ok) {
          setLeadFormError({
            message: leadResult.message || 'Lead submission failed. Please try again.',
          });
          applyRetryCooldown(leadResult.waitSeconds);
          return;
        }
        markLeadCaptured(formData.phone, leadResult.leadId);
      }

      await sendOtp(false);
      setOtpRetriesLeft(otpConfig.maxRetries);
      setOtpValue('');
      setFormStep('otp');
      setLeadFormError(null);
    } catch (error) {
      if (isLeadCapturedForPhone(formData.phone)) {
        setFormStep('otp');
        setOtpRetriesLeft(otpConfig.maxRetries);
        setOtpSendError(error.message, error.waitSeconds);
      } else {
        setLeadFormError({
          message: error.message || 'OTP could not be sent. Please try again.',
        });
        if (error.waitSeconds) applyRetryCooldown(error.waitSeconds);
      }
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleOtpVerify = async () => {
    if (otpValue.length !== 6) return;
    if (submitInFlightRef.current || isSubmitting) return;
    if (actionRetryTimer > 0) return;

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    clearOtpErrors();

    try {
      const response = await fetch(apiUrl('/api/otp/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...buildLeadPayload(formData, activeTemplate),
          phone: formData.phone,
          otp: otpValue,
          maxRetries: otpConfig.maxRetries,
          leadId: getCapturedLeadId(formData.phone),
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        setOtpVerifyError(result.message, result.waitSeconds);
        if (result.retriesLeft !== undefined) setOtpRetriesLeft(result.retriesLeft);
        return;
      }

      clearLeadCaptured(formData.phone);
      setCooldown(formData.phone, activeTemplate);
      markSessionSuccess();
      setFormStep('success');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
    } catch {
      setOtpVerifyError('OTP verification failed.');
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleOtpResend = async () => {
    if (resendTimer > 0) return;
    if (submitInFlightRef.current || isSubmitting) return;

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    clearOtpErrors();

    try {
      await sendOtp(true);
      setOtpValue('');
      setOtpRetriesLeft(otpConfig.maxRetries);
    } catch (error) {
      setOtpSendError(error.message, error.waitSeconds);
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  // What to show the patient as "the package you're booking", right above
  // the Name/Phone/City fields — a specific "Book Now" click wins (see
  // handleSubmit's selectedCatalogId comment above for why), otherwise this
  // falls back to the page's single default item configured in Admin's Form
  // Settings tab (formSettings.booking_item_name), so the patient always
  // sees which item they're about to book, even via the hero form directly.
  const selectedItemLabel = isBookingTemplate
    ? (selectedPackage?.title || formSettings.booking_item_name || null)
    : null;

  const formPanelProps = {
    hero,
    cities,
    otpConfig,
    formData,
    isBookingTemplate,
    onInputChange: handleInputChange,
    formStep,
    isFormValid,
    phoneError,
    isSubmitting,
    otpValue,
    otpError,
    otpErrorHint,
    otpRetriesLeft,
    resendTimer,
    leadFormError,
    actionRetryTimer,
    onSubmit: handleSubmit,
    onOtpChange: handleOtpChange,
    onOtpVerify: handleOtpVerify,
    onOtpResend: handleOtpResend,
    selectedItemLabel,
  };

  const testimonialFooter = (
    <div className="bg-[#eef8ff] p-4 flex items-center gap-3 border-t border-blue-50">
      <div className="relative shrink-0">
        <img
          src="https://phlebo-india.s3.ap-south-1.amazonaws.com/curelo-health/Shreyas.png"
          alt="Shreyas Iyer"
          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm object-top"
        />
      </div>
      <div className="flex-1">
        <p className="text-xs font-bold text-[#0f172a] leading-tight mb-1 italic">
          "I trust Curelo for me and my family"
        </p>
        <div>
          <p className="text-[10px] font-bold text-gray-800">Shreyas Iyer</p>
          <p className="text-[9px] text-gray-500">Indian Cricketer</p>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <section className="bg-[#f2f4f7] pt-2 pb-4 lg:py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-6 items-start">

            <div className="w-full lg:w-[65%] flex flex-col gap-3 lg:gap-8">
              <div className="relative rounded-xl lg:rounded-2xl overflow-hidden shadow-sm bg-white group">
                {hero.showHeroContent !== false && (
                  <div className="absolute inset-0 flex flex-col justify-center px-6 lg:px-12 z-10 bg-gradient-to-r from-black/60 via-black/40 to-transparent">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 lg:mb-5 drop-shadow-md leading-tight max-w-lg">
                        {(() => {
                          const heading = hero.heroHeading || "Your Health, {Our Priority}";
                          return heading.split(/(\{.*?\})/g).map((part, index) => {
                            if (part.startsWith('{') && part.endsWith('}')) {
                              return <span key={index} className="text-green-400">{part.slice(1, -1)}</span>;
                            }
                            return part;
                          });
                        })()}
                      </h1>
                      <p className="text-sm md:text-base lg:text-lg text-gray-100 max-w-md drop-shadow-md leading-relaxed">
                        {hero.heroDescription || "Experience accurate diagnostics, on-time collections, and reliable care tailored for you."}
                      </p>
                    </motion.div>
                  </div>
                )}

                {isSmallDevice && (
                  <img src={getImageSrc(hero.smallBanner, 'smallBanner')} alt="Banner" fetchPriority="high" decoding="async" className="w-full h-auto min-h-[300px] object-cover" onError={() => handleImageError('smallBanner')} />
                )}
                {!isSmallDevice && (
                  <img src={getImageSrc(hero.mobileBanner, 'mobileBanner')} alt="Banner" fetchPriority="high" decoding="async" className="w-full h-auto min-h-[350px] object-cover lg:hidden" onError={() => handleImageError('mobileBanner')} />
                )}
                <img src={getImageSrc(hero.desktopBanner, 'desktopBanner')} alt="Banner" fetchPriority="high" decoding="async" className="w-full h-auto min-h-[400px] object-cover hidden lg:block" onError={() => handleImageError('desktopBanner')} />
              </div>

              <div className="lg:hidden">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                  {formStep !== 'success' && formStep !== 'otp' && (
                    <div className="pt-5 pb-2 px-5">
                      <h2 className="text-lg font-bold text-slate-800">{hero.formTitle || "Book Your Test Today"}</h2>
                    </div>
                  )}
                  {formStep === 'otp' && (
                    <div className="pt-5 pb-2 px-5">
                      <h2 className="text-lg font-bold text-slate-800">Verify OTP</h2>
                    </div>
                  )}
                  <FormPanel {...formPanelProps} isMobile />
                  {testimonialFooter}
                </div>
              </div>

              <div className="text-center lg:hidden">
                <h2 className="text-xl font-bold text-gray-800 mb-6 font-sans">{hero.uspsTitle || "Why Choose Curelo Health?"}</h2>
                <div className="grid grid-cols-2 gap-4 px-2">
                  {hero.usps.map((point, index) => (
                    <div key={index} className="flex flex-col items-center text-center gap-3">
                      <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center border border-gray-100 p-3">
                        <img src={point.icon} alt={point.title} className="w-full h-full object-contain" />
                      </div>
                      <h3 className="text-xs font-semibold text-gray-800 leading-tight max-w-[140px]">{point.title}</h3>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-center hidden lg:block">
                <h2 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 font-sans">{hero.uspsTitle || "Why Choose Curelo Health?"}</h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-2">
                  {hero.usps.map((point, index) => (
                    <div key={index} className="flex flex-col items-center text-center gap-3">
                      <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center border border-gray-100 p-3">
                        <img src={point.icon} alt={point.title} className="w-full h-full object-contain" onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/64?text=Icon'; }} />
                      </div>
                      <h3 className="text-xs lg:text-sm font-semibold text-gray-800 leading-tight max-w-[140px]">{point.title}</h3>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[35%] hidden lg:block">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 sticky top-4">
                {formStep !== 'success' && formStep !== 'otp' && (
                  <div className="pt-6 pb-2 px-6">
                    <h2 className="text-xl font-bold text-slate-800">{hero.formTitle || "Book Your Test Today"}</h2>
                  </div>
                )}
                {formStep === 'otp' && (
                  <div className="pt-6 pb-2 px-6">
                    <h2 className="text-xl font-bold text-slate-800">Verify OTP</h2>
                  </div>
                )}
                <FormPanel {...formPanelProps} isMobile={false} />
                {testimonialFooter}
              </div>
            </div>
          </div>
        </div>
      </section>

      {showToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 px-5 py-4 flex items-start gap-3 max-w-sm">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-800">Success</p>
              <p className="text-sm text-gray-600">Thank you for reaching out. Our support team will contact you soon.</p>
            </div>
            <button onClick={() => setShowToast(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroSection;
